/**
 * Patterns for errors that are expected during normal operation and should
 * NOT be reported to Sentry.  Each entry documents *why* the error is
 * expected so the list stays intentional and auditable.
 */
const EXPECTED_ERROR_PATTERNS: ReadonlyArray<{ pattern: string; reason: string }> = [
  {
    pattern: 'fetch failed',
    reason:
      'Transient network failure (e.g. DNS timeout, connection reset). ' +
      'Retrying or refreshing resolves it; not actionable in Sentry.'
  },
  {
    pattern: 'Failed to fetch',
    reason: 'Browser-native variant of "fetch failed". Same transient network error.'
  },
  {
    pattern: 'NavigationDuplicated',
    reason: 'Vue Router 3.x throws when navigating to the current route. Not a bug.'
  },
  {
    pattern: 'Avoided redundant navigation',
    reason: 'Message body of NavigationDuplicated errors.'
  },
  {
    pattern: 'Navigation aborted',
    reason: 'Navigation guards redirecting users — expected control flow, not an error.'
  },
  {
    pattern: 'Redirected when going from',
    reason: 'Navigation guards redirecting users — expected control flow, not an error.'
  },
  {
    pattern: 'The user aborted a request',
    reason: 'User navigated away before fetch completed (AbortError). Not actionable.'
  },
  {
    pattern: 'Signature has expired',
    reason: 'JWT/session expired. UI redirects to login. Not actionable in Sentry.'
  },
  {
    pattern: 'Invariant Violation: 16',
    reason:
      'Apollo clearStore() rejects in-flight queries during logout. ' +
      'Expected and harmless — queries are irrelevant after store reset.'
  }
]

/**
 * Returns `true` when `message` matches a known expected-error pattern.
 * Use this in Sentry `beforeSend` and SSR error middleware to keep
 * filtering consistent.  Store catch blocks should always show toasts
 * so users get feedback on failures.
 */
export function isExpectedSentryError(message: string): boolean {
  return EXPECTED_ERROR_PATTERNS.some(({ pattern }) => message.includes(pattern))
}
